import { SalonApplication } from '../application/salon-application.js';
import { emptySalonWorkingSet, localDateKey, starterStaff } from '../application/salon-defaults.js';
import type { FirstRunCommand, FirstRunOutcome, SystemStatusSummary } from '../application/contracts.js';
import { BrowserBackupService } from './backup.js';
import { AutoDriveBackupCoordinator } from './auto-drive-backup.js';
import { GoogleDriveBackupAdapter } from './drive.js';
import { IndexedDbSalonStore } from './indexeddb-store.js';
import { createMaintenanceSummary, maintenanceDownload, serviceWorkerMaintenanceStatus } from './maintenance-summary.js';
import type { LocalPhotoView, SalonLocalApi } from './global.js';

const report = (stage: string, details: Record<string, unknown> = {}): void => console.info(`[calcomate] ${stage}`, details);
const store = new IndexedDbSalonStore(new Date(), report, false);
await store.initialize();
if (!store.isWriter) {
  const banner = document.createElement('div'); banner.className = 'local-banner'; banner.textContent = 'Calcomate Salon başka bir sekmede açık. Veriyi korumak için bu sekme salt okunur.'; document.body.prepend(banner);
}
const application = new SalonApplication(store, 'production');
const backups = new BrowserBackupService(store), drive = new GoogleDriveBackupAdapter(store, backups), autoBackups = new AutoDriveBackupCoordinator(store, drive);
await autoBackups.start();
addEventListener('pagehide', () => autoBackups.stop(), { once: true });

window.salonApplication = application;
const systemStatus = async (): Promise<SystemStatusSummary> => {
  const initialized = await store.hasState();
  return {
    applicationAvailable: initialized,
    firstRunRequired: !initialized,
    database: { state: 'ready', label: store.isWriter ? 'Yerel veritabanı hazır' : 'Salt okunur sekme' },
    components: [
      { label: 'IndexedDB yerel veri', state: 'ready' },
      { label: 'Çevrimdışı uygulama kabuğu', state: 'ready' },
      { label: 'Şifreli yedek', state: 'ready' },
      { label: 'Google Drive yedeği', state: (await drive.configuredClientId()) ? 'ready' : 'skipped' }
    ], message: store.isWriter ? null : 'Salon başka bir sekmede açık. Bu sekme veriyi korumak için salt okunur.'
  };
};
const completeFirstRun = async (command: FirstRunCommand): Promise<FirstRunOutcome> => {
  const name = command.salonName.trim(), staffInput = command.staff.filter((person) => person.displayName.trim());
  if (name.length < 2) return { ok: false, error: { code: 'FIRST_RUN_NAME_INVALID', message: 'Salon adı en az 2 karakter olmalıdır.' } };
  if (!staffInput.length) return { ok: false, error: { code: 'FIRST_RUN_STAFF_REQUIRED', message: 'En az bir çalışan ekleyin.' } };
  const businessId = crypto.randomUUID(), snapshot = emptySalonWorkingSet({ businessId, businessName: name, today: localDateKey(new Date()), timezone: 'Europe/Istanbul' });
  snapshot.staff = starterStaff(businessId, staffInput);
  snapshot.employeeProfiles = snapshot.staff.map((person) => ({ staffId: person.id, phone: '', active: true, baseSalaryMinor: 0, note: '', version: 1 }));
  await store.initializeWith(snapshot);
  return { ok: true };
};
window.salonRuntime = { report, relaunch: () => location.reload(), getSystemStatus: systemStatus, completeFirstRun };

const api: SalonLocalApi = {
  async storageStatus() { const estimate = await navigator.storage?.estimate?.(), persisted = await navigator.storage?.persisted?.() ?? false; return { writer: store.isWriter, persisted, usage: estimate?.usage ?? null, quota: estimate?.quota ?? null, origin: location.origin, online: navigator.onLine }; },
  backupStatus: () => backups.status(),
  autoDriveBackupStatus: () => autoBackups.status(),
  enableAutoDriveBackup: (password) => autoBackups.enable(password),
  disableAutoDriveBackup: () => autoBackups.disable(),
  exportBackup: (password) => backups.download(password),
  async restoreBackup(file, password) { await backups.restore(file, password); location.reload(); },
  async listPhotos(clientPartyId) { return (await store.listPhotos(clientPartyId)).map((photo): LocalPhotoView => { const { blob, ...meta } = photo; return { ...meta, url: URL.createObjectURL(blob) }; }); },
  async addPhoto(clientPartyId, kind, file, note) { await store.addPhoto({ clientPartyId, kind, name: file.name, mimeType: file.type, size: file.size, note: note.trim(), blob: file }); },
  deletePhoto: (id) => store.deletePhoto(id),
  driveClientId: () => drive.configuredClientId(),
  connectDrive: (clientId) => drive.connect(clientId),
  async disconnectDrive() { await drive.disconnect(); await autoBackups.driveDisconnected(); },
  driveBackup: (password) => autoBackups.runManual(password),
  async driveRestoreLatest(password) { await drive.restoreLatest(password); location.reload(); },
  driveConnected: () => drive.connected,
  async maintenanceSummary(format) {
    const [storage, backup, automatic, update] = await Promise.all([
      api.storageStatus(), backups.status(), autoBackups.status(), serviceWorkerMaintenanceStatus()
    ]);
    return maintenanceDownload(createMaintenanceSummary({ generatedAt: new Date().toISOString(), storage, backup, automatic, update }), format);
  }
};
window.salonLocal = api;

if ('serviceWorker' in navigator) addEventListener('load', () => { void navigator.serviceWorker.register('./service-worker.js'); });
void navigator.storage?.persist?.();
await import('../ui/renderer.js');
